import { useState } from 'react';

import { Box, Table, Text, useMantineTheme } from '@mantine/core';

import { IconArrowRight, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../../../../../shared/components/UI/Button/Button';
import { EmptyPlaceholder } from '../../../../../../shared/components/UI/EmptyPlaceholder/EmptyPlaceholder';
import { TenderTitle } from '../../../../../../shared/components/UI/TenderTitle/TenderTitle';

import { useUrlHash } from '../../../../../../shared/contexts/UrlHash.provider';
import type { BuyerSimulationBidResponse } from '../../../../../../shared/entities/BuyerSimBidResponse';
import { UrlHashBuilder } from '../../../../../../shared/utils/navigation/UrlHashBuilder';
import { useBuyerSimBidResponseList } from '../../hooks/useBuyerSimBidResponseList.hook';
import { CreateBuyerSimulationBidResponseModal } from './CreateBuyerSimBidResponseModal';

export const BuyerSimBidResponseList = () => {
  const { buyerSimBidResponseList } = useBuyerSimBidResponseList();
  const theme = useMantineTheme();

  if (buyerSimBidResponseList.length === 0) {
    return <EmptyBuyerSimulationBidResponseList />;
  }

  return (
    <Box
      py="04"
      px="05"
      bg="white"
      sx={theme => ({
        border: `1px solid ${theme.colors.gray[2]}`,
        borderRadius: theme.radius.md,
      })}
    >
      <Table highlightOnHover>
        <thead>
          <tr>
            <th style={{ borderBottom: `1px solid ${theme.colors.gray[1]}` }}>
              <Text variant="sm" fw="500" c="gray.6">
                Opportunités
              </Text>
            </th>
            <th style={{ borderBottom: `1px solid ${theme.colors.gray[1]}` }}>
              <Text variant="sm" fw="500" c="gray.6">
                Actions
              </Text>
            </th>
          </tr>
        </thead>
        <tbody>
          {buyerSimBidResponseList.map(buyerSimBidResponse => (
            <BuyerSimulationBidResponseRow
              key={buyerSimBidResponse.id}
              buyerSimBidResponse={buyerSimBidResponse}
            />
          ))}
        </tbody>
      </Table>
    </Box>
  );
};

type BuyerSimulationBidResponseRowProps = {
  buyerSimBidResponse: BuyerSimulationBidResponse;
};

const BuyerSimulationBidResponseRow = ({
  buyerSimBidResponse,
}: BuyerSimulationBidResponseRowProps) => {
  const theme = useMantineTheme();
  const { redirectTo } = useUrlHash();
  const tdStyling = {
    width: '90%',
    padding: theme.spacing['02'],
    margin: 0,
    borderTop: `1px solid ${theme.colors.gray[1]}`,
    cursor: 'pointer',
  };

  const handleClick = (e: React.MouseEvent) => {
    redirectTo(
      `/response/simulation/${buyerSimBidResponse.id}`,
      new UrlHashBuilder().addLocationData().build(),
      undefined,
      e,
    );
  };

  return (
    <tr onClick={handleClick}>
      <td style={tdStyling}>
        <TenderTitle
          buyerOriginalName={buyerSimBidResponse.buyerName}
          tenderTitle={buyerSimBidResponse.title}
          size="md"
        />
      </td>
      <td style={tdStyling}>
        <Button variant="light" size="sm" leftIcon={<IconArrowRight />}>
          Ouvrir
        </Button>
      </td>
    </tr>
  );
};

const EmptyBuyerSimulationBidResponseList = () => {
  const [
    createBuyerSimBidResponseModalOpenned,
    setCreateBuyerSimBidResponseModalOpenned,
  ] = useState(false);
  const { t } = useTranslation(['buyerSimulation']);
  const AddBuyerSimBidResponseBtn = (
    <Button
      leftIcon={<IconPlus />}
      onClick={() => setCreateBuyerSimBidResponseModalOpenned(true)}
    >
      {t('CTA')}
    </Button>
  );
  return (
    <Box
      py="04"
      px="05"
      bg="white"
      sx={theme => ({
        border: `1px solid ${theme.colors.gray[2]}`,
        borderRadius: theme.radius.md,
      })}
    >
      <CreateBuyerSimulationBidResponseModal
        opened={createBuyerSimBidResponseModalOpenned}
        closeModal={() => setCreateBuyerSimBidResponseModalOpenned(false)}
      />
      <EmptyPlaceholder
        title="Vous n'avez aucun projet de réponse en cours"
        actionsComponent={AddBuyerSimBidResponseBtn}
      />
    </Box>
  );
};
